import React, { useState, useEffect } from 'react';
import { notify, confirmDialog } from '../ui/notify';
import { apiCall, getApiBaseUrl, authHeaders } from '../config';

function GeneralDocuments({ user }) {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [newDocument, setNewDocument] = useState({
    title: '',
    description: '',
    category: 'Allgemein',
    file: null
  });
  const [showUploadForm, setShowUploadForm] = useState(false);

  const categories = [
    'Übersichtsplan',
    'Ablaufplan', 
    'Hinweise',
    'Richtlinien',
    'Notfallkontakte',
    'Allgemein'
  ];

  useEffect(() => {
    fetchDocuments();
  }, []);

  const fetchDocuments = async () => {
    try {
      const data = await apiCall('/general-documents');
      setDocuments(data);
    } catch (error) {
      console.error('Fehler beim Laden der Dokumente:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFileChange = (e) => {
    setNewDocument({ ...newDocument, file: e.target.files[0] });
  };

  const handleInputChange = (e) => {
    setNewDocument({ ...newDocument, [e.target.name]: e.target.value });
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    setUploading(true);
    
    try {
      // Erstelle FormData aus dem Formular
      const formData = new FormData();
      formData.append('title', newDocument.title);
      formData.append('description', newDocument.description);
      formData.append('category', newDocument.category);
      formData.append('document', newDocument.file);
      
      // Verwende direkten fetch() für File-Upload, nicht apiCall()
      const baseUrl = getApiBaseUrl();
      const response = await fetch(`${baseUrl}/general-documents/upload`, {
        method: 'POST',
        body: formData,
        // Keine Content-Type Header setzen - lasse Browser das automatisch machen für multipart/form-data
        headers: { ...authHeaders() },
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Upload failed: ${response.status} - ${errorText}`);
      }

      const result = await response.json();
      notify('Dokument erfolgreich hochgeladen!');
      setNewDocument({ title: '', description: '', category: 'Allgemein', file: null });
      setShowUploadForm(false);
      fetchDocuments();
        
      // Reset file input
      const fileInput = document.querySelector('input[type="file"]');
      if (fileInput) fileInput.value = '';
    } catch (error) {
      console.error('Upload error:', error);
      notify('Fehler beim Hochladen der Datei: ' + error.message);
    } finally {
      setUploading(false);
    }
  };

  const handleDownload = async (documentId, filename) => {
    try {
      // Verwende die gleiche API-Basis-URL wie andere Calls
      const baseUrl = getApiBaseUrl();
      const response = await fetch(`${baseUrl}/general-documents/download/${documentId}`, { headers: authHeaders() });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      // Erstelle einen Blob vom Response
      const blob = await response.blob();
      
      // Erstelle eine temporäre URL für den Download
      const url = window.URL.createObjectURL(blob);
      
      // Erstelle einen temporären Download-Link
      const a = document.createElement('a');
      a.style.display = 'none';
      a.href = url;
      a.download = filename || `document_${documentId}`;
      
      // Füge den Link zum DOM hinzu, klicke ihn und entferne ihn wieder
      document.body.appendChild(a);
      a.click();
      
      // Räume auf
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      console.log(`Dokument heruntergeladen: ${filename}`);
    } catch (error) {
      console.error('Download error:', error);
      notify('Fehler beim Herunterladen der Datei: ' + error.message);
    }
  };

  const handleDelete = async (documentId, title) => {
    if (!await confirmDialog(`Dokument "${title}" wirklich löschen?`)) {
      return;
    }

    try {
      await apiCall(`/general-documents/${documentId}`, {
        method: 'DELETE'
      });

      notify('Dokument erfolgreich gelöscht');
      fetchDocuments();
    } catch (error) {
      console.error('Delete error:', error);
      notify('Fehler beim Löschen des Dokuments: ' + (error.message || 'Unbekannter Fehler'));
    }
  };

  const getCategoryIcon = (category) => {
    switch (category) {
      case 'Übersichtsplan': return 'bi-diagram-3';
      case 'Ablaufplan': return 'bi-list-check';
      case 'Hinweise': return 'bi-exclamation-triangle';
      case 'Richtlinien': return 'bi-book';
      case 'Notfallkontakte': return 'bi-telephone';
      default: return 'bi-file-earmark-text';
    }
  };

  const getCategoryColor = (category) => {
    switch (category) {
      case 'Übersichtsplan': return 'primary';
      case 'Ablaufplan': return 'success';
      case 'Hinweise': return 'warning';
      case 'Richtlinien': return 'info';
      case 'Notfallkontakte': return 'danger';
      default: return 'secondary';
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="text-center py-4">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Lade...</span>
        </div>
        <p className="mt-2 text-muted">Lade Dokumente...</p>
      </div>
    );
  }

  return (
    <div className="card border-0 shadow-sm">
      <div className="card-header bg-white d-flex justify-content-between align-items-center">
        <h5 className="mb-0">
          <i className="bi bi-folder-fill me-2 text-primary"></i>
          Allgemeine Dokumente
        </h5>
        {user.role === 'admin' && (
          <button 
            className="btn btn-primary btn-sm"
            onClick={() => setShowUploadForm(!showUploadForm)}
          >
            <i className="bi bi-plus-circle me-1"></i>
            Dokument hochladen
          </button>
        )}
      </div>
      
      <div className="card-body">
        {/* Upload Form für Admin */}
        {user.role === 'admin' && showUploadForm && (
          <div className="mb-4 p-3 bg-light rounded">
            <h6><i className="bi bi-cloud-upload me-2"></i>Neues Dokument hochladen</h6>
            <form onSubmit={handleUpload}>
              <div className="row">
                <div className="col-md-6">
                  <div className="mb-3">
                    <label className="form-label">Titel*</label>
                    <input
                      type="text"
                      className="form-control"
                      name="title"
                      value={newDocument.title}
                      onChange={handleInputChange}
                      placeholder="z.B. Übersichtsplan September 2025"
                      required
                    />
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="mb-3">
                    <label className="form-label">Kategorie</label>
                    <select
                      className="form-select"
                      name="category"
                      value={newDocument.category}
                      onChange={handleInputChange}
                    >
                      {categories.map(cat => (
                        <option key={cat} value={cat}>{cat}</option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
              
              <div className="mb-3">
                <label className="form-label">Beschreibung</label>
                <textarea
                  className="form-control"
                  name="description"
                  value={newDocument.description}
                  onChange={handleInputChange}
                  rows="2"
                  placeholder="Kurze Beschreibung des Dokuments..."
                />
              </div>
              
              <div className="mb-3">
                <label className="form-label">Datei*</label>
                <input
                  type="file"
                  className="form-control"
                  onChange={handleFileChange}
                  accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                  required
                />
                <small className="form-text text-muted">
                  Erlaubte Dateitypen: PDF, Word, Bilder (max. 10MB)
                </small>
              </div>
              
              <div className="d-flex gap-2">
                <button 
                  type="submit" 
                  className="btn btn-primary"
                  disabled={uploading}
                >
                  {uploading ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2"></span>
                      Lade hoch...
                    </>
                  ) : (
                    <>
                      <i className="bi bi-upload me-1"></i>
                      Hochladen
                    </>
                  )}
                </button>
                <button 
                  type="button" 
                  className="btn btn-secondary"
                  onClick={() => setShowUploadForm(false)}
                >
                  Abbrechen
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Dokumente Liste */}
        {documents.length > 0 ? (
          <div className="row">
            {documents.map((doc) => (
              <div key={doc.id} className="col-md-6 col-lg-4 mb-3">
                <div className="card h-100 border-1">
                  <div className="card-body">
                    <div className="d-flex justify-content-between align-items-start mb-2">
                      <span className={`badge bg-${getCategoryColor(doc.category)} rounded-pill`}>
                        <i className={`${getCategoryIcon(doc.category)} me-1`}></i>
                        {doc.category}
                      </span>
                      {user.role === 'admin' && (
                        <button
                          className="btn btn-outline-danger btn-sm"
                          onClick={() => handleDelete(doc.id, doc.title)}
                          title="Dokument löschen"
                        >
                          <i className="bi bi-trash"></i>
                        </button>
                      )}
                    </div>
                    
                    <h6 className="card-title">{doc.title}</h6>
                    
                    {doc.description && (
                      <p className="card-text text-muted small">{doc.description}</p>
                    )}
                    
                    {doc.original_name && (
                      <small className="text-muted d-block mb-2">
                        <i className="bi bi-file-earmark me-1"></i>
                        {doc.original_name}
                        {doc.file_size && (
                          <span className="ms-2">
                            ({Math.round(doc.file_size / 1024)} KB)
                          </span>
                        )}
                      </small>
                    )}
                    
                    <div className="d-flex justify-content-between align-items-center mt-auto">
                      <small className="text-muted">
                        <i className="bi bi-calendar3 me-1"></i>
                        {formatDate(doc.uploaded_at)}
                      </small>
                      <button
                        className="btn btn-primary btn-sm"
                        onClick={() => handleDownload(doc.id, doc.original_name || doc.filename)}
                      >
                        <i className="bi bi-download me-1"></i>
                        Download
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-4">
            <i className="bi bi-folder2-open text-muted" style={{fontSize: '3rem'}}></i>
            <p className="mt-2 text-muted">Noch keine Dokumente verfügbar</p>
            {user.role === 'admin' && (
              <p className="text-muted small">
                Laden Sie Dokumente wie Übersichtspläne, Ablaufpläne oder wichtige Hinweise hoch,
                damit alle Mitarbeiter darauf zugreifen können.
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default GeneralDocuments;
